import type { ReactNode } from 'react';
import { Switch } from './Switch.js';
import './ToggleRow.css';

export interface ToggleRowProps {
  title: ReactNode;
  /** A sentence of consequence. Most prototype rows have one. */
  note?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /**
   * Chrome. The prototype uses all three, in the same product:
   *
   *   `card`  a bordered 12px-radius row (Source detail objects, Bulk approve filters)
   *   `bare`  no border, no padding — a list inside a card that already has both
   *   `table` full-bleed with a bottom hairline, as a `DataTable`-style row
   */
  variant?: 'card' | 'bare' | 'table';
  /**
   * Renders **on and refuses to toggle** — the prototype's `locked: true` object
   * rows, where a field is read because identity resolution requires it.
   *
   * Deliberately not the same as `disabled`: a disabled switch reads as "not
   * available yet", while this one is *deliberately and permanently on*. The row
   * says why in its `lockedReason`, because a control that ignores a click without
   * explanation is the most irritating thing a settings screen can do.
   */
  locked?: boolean;
  lockedReason?: ReactNode;
  disabled?: boolean;
  /**
   * Trailing content — the prototype puts three different things here: an effect
   * delta (`−6%`), an "Always on" badge, or a count plus a last-read timestamp.
   */
  trailing?: ReactNode;
  /** Aligns the switch to the top when the note runs to several lines. */
  align?: 'center' | 'start';
}

/**
 * A row whose whole surface is a switch (design-system catalogue, Tier 1).
 *
 * **4 of the prototype's 18 page files.** `Switch` ships as a label beside a track,
 * which is right for a single setting in a form; every prototype use is a *row* —
 * title, consequence, and often a trailing figure — where the row is the control
 * (prototype/CENSUS.md §2a item 13).
 *
 * It **composes `Switch`** rather than redrawing the track, so there is one real
 * `<input role="switch">` in the product and one place its states are styled. The
 * visible title and note live outside that input's own label, and the whole row is
 * a `<label>`, which is what makes clicking the note toggle the switch — no
 * `onClick` on a div, no synthetic keyboard handling.
 */
export function ToggleRow({
  title,
  note,
  checked,
  onChange,
  variant = 'card',
  locked,
  lockedReason,
  disabled,
  trailing,
  align = 'center',
}: ToggleRowProps) {
  const inert = locked || disabled;
  return (
    <div
      className={[
        'dpr-toggle-row',
        `dpr-toggle-row--${variant}`,
        `dpr-toggle-row--${align}`,
        locked ? 'dpr-toggle-row--locked' : '',
        disabled ? 'dpr-toggle-row--disabled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Switch
        className="dpr-toggle-row__switch"
        checked={locked ? true : checked}
        onChange={(e) => {
          if (inert) return;
          onChange(e.currentTarget.checked);
        }}
        /* `readOnly` rather than `disabled` for a locked row: a disabled control is
           removed from the tab order and announced as unavailable, which is the
           wrong story — this one is permanently ON and the user should be able to
           reach it and hear why. */
        readOnly={locked}
        disabled={disabled}
        label={
          <span className="dpr-toggle-row__text">
            <span className="dpr-toggle-row__title">{title}</span>
            {note ? <span className="dpr-toggle-row__note">{note}</span> : null}
            {locked && lockedReason ? (
              <span className="dpr-toggle-row__note dpr-toggle-row__note--locked">
                {lockedReason}
              </span>
            ) : null}
          </span>
        }
      />
      {trailing ? <span className="dpr-toggle-row__trailing">{trailing}</span> : null}
    </div>
  );
}
